<template>
  <div class="p-4 space-y-4">
    <!-- Encabezado -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-xl font-bold text-gray-900 dark:text-gray-100">Presupuestos</h1>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Gestiona tus presupuestos y compara con gastos reales</p>
      </div>
      <button
        @click="abrirModalNuevo"
        class="px-3 py-1.5 text-sm font-medium rounded-md bg-violet-600 text-white hover:bg-violet-700 transition-colors flex items-center gap-1.5"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Nuevo Presupuesto
      </button>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Año</label>
          <input
            v-model.number="filtros.anio"
            type="number"
            min="2020"
            max="2100"
            @change="cargarPresupuestos"
            class="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Periodo</label>
          <select
            v-model="filtros.periodo"
            @change="cargarPresupuestos"
            class="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option :value="null">Todos</option>
            <option value="mensual">Mensual</option>
            <option value="trimestral">Trimestral</option>
            <option value="semestral">Semestral</option>
            <option value="anual">Anual</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Categoría</label>
          <select
            v-model="filtros.categoria_id"
            @change="cargarPresupuestos"
            class="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option :value="null">Todas</option>
            <option v-for="cat in categorias" :key="cat.id" :value="cat.id">{{ cat.nombre }}</option>
          </select>
        </div>
        <div class="flex items-end">
          <button
            @click="verComparativa"
            class="w-full px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Ver Comparativa
          </button>
        </div>
      </div>
    </div>

    <!-- Estado de carga -->
    <div v-if="cargando" class="text-center py-8">
      <div class="inline-block w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
    </div>

    <!-- Tabla de presupuestos -->
    <div v-else-if="presupuestos.length > 0" class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th class="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Categoría</th>
              <th class="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Periodo</th>
              <th class="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Año</th>
              <th class="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Mes</th>
              <th class="px-4 py-2.5 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Monto Estimado</th>
              <th class="px-4 py-2.5 text-center text-xs font-semibold text-gray-700 dark:text-gray-300">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-800">
            <tr v-for="presupuesto in presupuestos" :key="presupuesto.id" class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded-full" :style="{ backgroundColor: presupuesto.categoria?.color || '#6B7280' }"></div>
                  <span class="text-sm text-gray-900 dark:text-gray-100">{{ presupuesto.categoria?.nombre || 'Sin categoría' }}</span>
                </div>
              </td>
              <td class="px-4 py-3">
                <span class="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                  {{ presupuesto.periodo }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{{ presupuesto.anio }}</td>
              <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{{ presupuesto.mes ? getNombreMes(presupuesto.mes) : '-' }}</td>
              <td class="px-4 py-3 text-right">
                <EtiquetaMonto :monto="presupuesto.monto_estimado" size="sm" bold />
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center justify-center gap-2">
                  <button
                    @click="abrirModalEditar(presupuesto)"
                    class="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                    title="Editar"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    @click="eliminarPresupuesto(presupuesto.id)"
                    class="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    title="Eliminar"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot class="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <tr>
              <td colspan="4" class="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Total Presupuestado</td>
              <td class="px-4 py-3 text-right">
                <EtiquetaMonto :monto="totalPresupuestado" size="base" bold />
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Estado vacío -->
    <div v-else class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-12 text-center">
      <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
      <p class="text-sm text-gray-500 dark:text-gray-400">No hay presupuestos registrados</p>
      <button
        @click="abrirModalNuevo"
        class="mt-4 px-4 py-2 text-sm font-medium rounded-md bg-violet-600 text-white hover:bg-violet-700 transition-colors"
      >
        Crear primer presupuesto
      </button>
    </div>

    <!-- Modal de Comparativa -->
    <div v-if="mostrarComparativa && comparativa" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 class="text-lg font-bold text-gray-900 dark:text-gray-100">Comparativa: Presupuesto vs Real</h2>
          <button @click="mostrarComparativa = false" class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div class="mb-4 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Periodo:</strong> {{ comparativa.periodo }}</p>
            <p><strong>Año:</strong> {{ comparativa.anio }}</p>
            <p v-if="comparativa.mes"><strong>Mes:</strong> {{ getNombreMes(comparativa.mes) }}</p>
            <p><strong>Rango:</strong> {{ comparativa.fecha_inicio }} a {{ comparativa.fecha_fin }}</p>
          </div>

          <div class="space-y-3">
            <div v-for="item in comparativa.comparativas" :key="item.presupuesto.id"
              class="border rounded-lg p-4"
              :class="{
                'border-green-300 bg-green-50 dark:bg-green-900/20': item.estado === 'normal',
                'border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20': item.estado === 'alerta',
                'border-red-300 bg-red-50 dark:bg-red-900/20': item.estado === 'excedido'
              }"
            >
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded-full" :style="{ backgroundColor: item.presupuesto.categoria?.color || '#6B7280' }"></div>
                  <h3 class="font-semibold text-gray-900 dark:text-gray-100">{{ item.presupuesto.categoria?.nombre }}</h3>
                </div>
                <span class="text-xs px-2 py-1 rounded-full font-medium"
                  :class="{
                    'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200': item.estado === 'normal',
                    'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200': item.estado === 'alerta',
                    'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200': item.estado === 'excedido'
                  }"
                >
                  {{ item.estado === 'normal' ? 'Normal' : item.estado === 'alerta' ? 'Alerta' : 'Excedido' }}
                </span>
              </div>

              <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div>
                  <p class="text-xs text-gray-500 dark:text-gray-400">Presupuestado</p>
                  <EtiquetaMonto :monto="item.presupuesto.monto_estimado" size="sm" bold />
                </div>
                <div>
                  <p class="text-xs text-gray-500 dark:text-gray-400">Gastado</p>
                  <EtiquetaMonto :monto="item.monto_real" size="sm" bold />
                </div>
                <div>
                  <p class="text-xs text-gray-500 dark:text-gray-400">Diferencia</p>
                  <EtiquetaMonto :monto="item.diferencia" size="sm" bold />
                </div>
                <div>
                  <p class="text-xs text-gray-500 dark:text-gray-400">% Usado</p>
                  <p class="text-sm font-bold"
                    :class="{
                      'text-green-600 dark:text-green-400': item.estado === 'normal',
                      'text-yellow-600 dark:text-yellow-400': item.estado === 'alerta',
                      'text-red-600 dark:text-red-400': item.estado === 'excedido'
                    }"
                  >
                    {{ item.porcentaje_usado }}%
                  </p>
                </div>
              </div>

              <!-- Barra de progreso -->
              <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                <div
                  class="h-full transition-all"
                  :style="{ width: Math.min(item.porcentaje_usado, 100) + '%' }"
                  :class="{
                    'bg-green-600': item.estado === 'normal',
                    'bg-yellow-600': item.estado === 'alerta',
                    'bg-red-600': item.estado === 'excedido'
                  }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Crear/Editar -->
    <div v-if="modalAbierto" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-lg w-full">
        <div class="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 class="text-lg font-bold text-gray-900 dark:text-gray-100">
            {{ presupuestoEditando ? 'Editar Presupuesto' : 'Nuevo Presupuesto' }}
          </h2>
        </div>

        <div class="p-4 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Categoría *</label>
            <select
              v-model="formulario.categoria_id"
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option :value="null">Selecciona una categoría</option>
              <option v-for="cat in categorias" :key="cat.id" :value="cat.id">{{ cat.nombre }}</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monto Estimado *</label>
            <input
              v-model.number="formulario.monto_estimado"
              type="number"
              step="0.01"
              min="0"
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="0.00"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Periodo *</label>
            <select
              v-model="formulario.periodo"
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="mensual">Mensual</option>
              <option value="trimestral">Trimestral</option>
              <option value="semestral">Semestral</option>
              <option value="anual">Anual</option>
            </select>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Año *</label>
              <input
                v-model.number="formulario.anio"
                type="number"
                min="2020"
                max="2100"
                required
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mes (opcional)</label>
              <select
                v-model.number="formulario.mes"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option :value="null">-</option>
                <option v-for="m in 12" :key="m" :value="m">{{ getNombreMes(m) }}</option>
              </select>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción</label>
            <textarea
              v-model="formulario.descripcion"
              rows="2"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="Descripción del presupuesto (opcional)"
            ></textarea>
          </div>
        </div>

        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            @click="cerrarModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancelar
          </button>
          <button
            @click="guardarPresupuesto"
            :disabled="cargando"
            class="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-md hover:bg-violet-700 disabled:opacity-50 transition-colors"
          >
            {{ presupuestoEditando ? 'Actualizar' : 'Crear' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { usePresupuestos } from '@/composables/finanzas/usePresupuestos'
import EtiquetaMonto from '@/components/Compartidos/EtiquetaMonto.vue'

const {
  presupuestos,
  categorias,
  comparativa,
  cargando,
  filtros,
  totalPresupuestado,
  cargarPresupuestos,
  cargarCategorias,
  crearPresupuesto,
  actualizarPresupuesto,
  eliminarPresupuesto,
  obtenerComparativa
} = usePresupuestos()

const modalAbierto = ref(false)
const mostrarComparativa = ref(false)
const presupuestoEditando = ref(null)
const formulario = ref({
  categoria_id: null,
  monto_estimado: 0,
  periodo: 'mensual',
  anio: new Date().getFullYear(),
  mes: null,
  descripcion: ''
})

const abrirModalNuevo = () => {
  presupuestoEditando.value = null
  formulario.value = {
    categoria_id: null,
    monto_estimado: 0,
    periodo: 'mensual',
    anio: new Date().getFullYear(),
    mes: null,
    descripcion: ''
  }
  modalAbierto.value = true
}

const abrirModalEditar = (presupuesto) => {
  presupuestoEditando.value = presupuesto
  formulario.value = {
    categoria_id: presupuesto.categoria_id,
    monto_estimado: presupuesto.monto_estimado,
    periodo: presupuesto.periodo,
    anio: presupuesto.anio,
    mes: presupuesto.mes,
    descripcion: presupuesto.descripcion || ''
  }
  modalAbierto.value = true
}

const cerrarModal = () => {
  modalAbierto.value = false
  presupuestoEditando.value = null
}

const guardarPresupuesto = async () => {
  if (presupuestoEditando.value) {
    const exito = await actualizarPresupuesto(presupuestoEditando.value.id, formulario.value)
    if (exito) cerrarModal()
  } else {
    const exito = await crearPresupuesto(formulario.value)
    if (exito) cerrarModal()
  }
}

const verComparativa = async () => {
  await obtenerComparativa({
    periodo: filtros.value.periodo || 'mensual',
    mes: new Date().getMonth() + 1
  })
  mostrarComparativa.value = true
}

const getNombreMes = (num) => {
  const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
  return meses[num - 1] || ''
}

onMounted(async () => {
  await cargarCategorias()
  await cargarPresupuestos()
})
</script>
